import moment from 'moment';
import { fyStartMonth } from './financialYear';

/**
 * Like-for-like period comparison for the consumption cards.
 *
 * The cards compared a period-to-date against a whole previous period: on the
 * 5th of a month, five days of August against all thirty-one of July, reported
 * as a 84% collapse. The meter had not changed; the months were different
 * lengths. Every comparison here measures the same elapsed span on both sides.
 */

/** Periods the cards cover. */
export const Period = { DAY: 'day', WEEK: 'week', MONTH: 'month' };

/**
 * Boundary unit for each period.
 *
 * Weeks use isoWeek so they start on Monday, matching the `date_trunc('week')`
 * the backend aggregates with. Moment's own `startOf('week')` is Sunday, which
 * put the card's week and the query's week a day apart.
 */
const BOUNDARY = { day: 'day', week: 'isoWeek', month: 'month' };

/**
 * The elapsed span of the current period, and the matching span of the one
 * before it.
 *
 * @param {string} period - One of Period.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{current: {from: object, to: object}, prior: {from: object, to: object}}}
 */
export const comparableWindows = (period, now = () => moment()) => {
  const at = now().clone();
  const from = at.clone().startOf(BOUNDARY[period] || period);
  const elapsed = at.diff(from);

  const priorFrom = from.clone().subtract(1, period);

  return {
    current: { from, to: at },
    prior: { from: priorFrom, to: priorFrom.clone().add(elapsed, 'milliseconds') },
  };
};

/**
 * Sums the widget buckets falling inside a window.
 * @param {Array<object>} rows - Widget rows carrying `period_ts` and `sum_value`.
 * @param {{from: object, to: object}} window - Bounds to sum between.
 * @returns {number} Total for the window.
 */
export const sumWithin = (rows = [], window) => {
  if (!window) return 0;

  return (Array.isArray(rows) ? rows : []).reduce((total, row) => {
    if (!row?.period_ts) return total;

    const at = moment(row.period_ts);
    if (at.isBefore(window.from) || at.isSameOrAfter(window.to)) return total;

    const value = Number(row.sum_value ?? row.value);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
};

/**
 * Describes how far into the current period we are.
 * @param {string} period - One of Period.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string} A phrase such as '3 of 7 days elapsed'.
 */
export const elapsedLabel = (period, now = () => moment()) => {
  const at = now().clone();

  if (period === Period.DAY) return `to ${at.format('HH:mm')}`;

  const unit = BOUNDARY[period] || period;
  const from = at.clone().startOf(unit);
  const done = at.diff(from, 'days') + 1;
  const total = at.clone().endOf(unit).diff(from, 'days') + 1;

  return `${done} of ${total} days elapsed`;
};

/**
 * Names the span a card is compared against.
 * @param {string} period - One of Period.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string} A phrase such as 'the same 3 days last week'.
 */
export const baselineLabel = (period, now = () => moment()) => {
  const at = now().clone();

  if (period === Period.DAY) return `yesterday to ${at.format('HH:mm')}`;

  const done = at.diff(at.clone().startOf(BOUNDARY[period] || period), 'days') + 1;
  const noun = period === Period.WEEK ? 'last week' : 'last month';

  return done === 1 ? `the same day ${noun}` : `the same ${done} days ${noun}`;
};

/**
 * Compares a period to date against the same elapsed span before it.
 *
 * A baseline of zero yields no percentage. It cannot be told apart from a
 * period the meter never reported in, and a change measured against nothing is
 * either meaningless or unbounded — on this data the naive figure was −84% and
 * the like-for-like one +19,000%, neither of them true.
 *
 * @param {Array<object>} rows - Daily or hourly widget buckets spanning both windows.
 * @param {string} period - One of Period.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object} Value, baseline, change and the labels describing them.
 */
export const comparePeriod = (rows = [], period = Period.DAY, now = () => moment()) => {
  const { current, prior } = comparableWindows(period, now);

  const value = sumWithin(rows, current);
  const baseline = sumWithin(rows, prior);
  const comparable = baseline > 0;

  return {
    value,
    baseline,
    comparable,
    change: comparable ? ((value - baseline) / baseline) * 100 : null,
    changeValue: comparable ? value - baseline : null,
    elapsed: elapsedLabel(period, now),
    against: baselineLabel(period, now),
  };
};

/**
 * The first day of a financial year, from its "YYYY-YYYY" label.
 *
 * The year opens in the month the company reports from, matching the boundaries the readings are
 * tagged against.
 *
 * @param {string} financialYear - Value such as '2026-2027'.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object} A moment at the start of that year, or of the current one
 *   when the value cannot be read.
 */
export const financialYearStart = (financialYear, now = () => moment()) => {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(financialYear || '').trim());
  if (!match) return now().clone().startOf('year');

  return moment().year(Number(match[1])).month(fyStartMonth()).startOf('month');
};
